import type { RangeMetrics } from '../types/dashboard';

export type ValueFormat = 'int' | 'pct' | 'ratio' | 'currency';

export interface BenchmarkItem {
  id: string;
  label: string;
  entity: number;
  benchmark: number;
  format: ValueFormat;
  gapKind: 'relative' | 'pp';
  gap: number;
  severity: number;
  topic: string;
}

export interface BenchmarkInput {
  entity: RangeMetrics;
  bench: RangeMetrics;
  seasonRet: { entity: number | null; network: number | null };
  yoyRet: { entity: number | null; network: number | null };
}

/** Template discussion topics — deterministic, not AI-generated conclusions. */
const TOPICS: Record<string, string> = {
  members: 'Review website and paid-ad lead capture and local event reach in lower-performing ZIP codes.',
  registrations: 'Compare program calendar depth and capacity with peer territories.',
  conversion: 'Walk through follow-up cadence between a new member sign-up and the first registration.',
  revenue: 'Compare program mix and pricing tiers with peer territories.',
  regsPerMember: 'Discuss sibling and multi-program offers for families who already registered.',
  seasonRet: 'Review end-of-season re-enrollment offers and early-bird timing.',
  yoyRet: 'Review outreach to last year’s families before registration opens.',
};

export function buildBenchmarkItems(i: BenchmarkInput): BenchmarkItem[] {
  const out: BenchmarkItem[] = [];
  const add = (id: string, label: string, entity: number | null, benchmark: number | null, format: ValueFormat, gapKind: 'relative' | 'pp', unit: number) => {
    if (entity === null || benchmark === null || !isFinite(entity) || !isFinite(benchmark)) return;
    const gap = gapKind === 'pp' ? entity - benchmark : benchmark ? (entity - benchmark) / benchmark : 0;
    out.push({ id, label, entity, benchmark, format, gapKind, gap, severity: gap / unit, topic: TOPICS[id] });
  };
  add('members', 'Members', i.entity.members, i.bench.members, 'int', 'relative', 0.1);
  add('registrations', 'Registrations', i.entity.registrations, i.bench.registrations, 'int', 'relative', 0.1);
  add('conversion', 'Conversion rate', i.entity.conversionRate, i.bench.conversionRate, 'pct', 'pp', 0.03);
  add('revenue', 'Revenue', i.entity.revenue, i.bench.revenue, 'currency', 'relative', 0.1);
  add('regsPerMember', 'Registrations per member', i.entity.regsPerMember, i.bench.regsPerMember, 'ratio', 'relative', 0.05);
  add('seasonRet', 'Season retention (Summer → Fall 2026)', i.seasonRet.entity, i.seasonRet.network, 'pct', 'pp', 0.03);
  add('yoyRet', 'YOY retention (Fall 2025 → Fall 2026)', i.yoyRet.entity, i.yoyRet.network, 'pct', 'pp', 0.03);
  return out;
}
